import { useEffect, useState, useRef } from "react";
import { supabase } from "@/lib/supabaseClient";

export default function GlobalChat({ user }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [playerName, setPlayerName] = useState("");
  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const [open, setOpen] = useState(true); // ✅ default open

  const scrollToBottom = (force = false) => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
    if (force || isNearBottom) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  };

  // ✅ Mobile height handling
  useEffect(() => {
    const handleResize = () => {
      document.documentElement.style.setProperty(
        "--chat-vh",
        `${window.innerHeight * 0.4}px`
      );
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch player's display name
  useEffect(() => {
    const fetchPlayerName = async () => {
      if (!user?.id) return;
      const { data, error } = await supabase
        .from("game_users")
        .select("player_name, is_admin, role")
        .eq("user_id", user.id)
        .single();

      if (!error && data) {
        setPlayerName(data.player_name);
      }
    };
    fetchPlayerName();
  }, [user?.id]);

  // Initial load + realtime subscription
  useEffect(() => {
    const fetchMessages = async () => {
      let { data } = await supabase
        .from("chat_messages")
        .select("*")
        .order("created_at", { ascending: true })
        .limit(30);

      setMessages(data || []);
    };

    fetchMessages();

    const subscription = supabase
      .channel("public:chat_messages")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "chat_messages" },
        (payload) => {
          setMessages((prev) => {
            if (prev.some((m) => m.id === payload.new.id)) return prev;
            const withoutTemp = prev.filter(
              (m) =>
                !m.id.toString().startsWith("temp-") ||
                m.message !== payload.new.message ||
                m.user_id !== payload.new.user_id
            );
            const updated = [...withoutTemp, payload.new];
            return updated.slice(-30);
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
  }, []);

  useEffect(() => scrollToBottom(true), [messages]);

  // Optimistic send
  const sendMessage = async () => {
    if (!newMessage.trim() || !playerName) return;
    const text = newMessage.trim();

    const tempMessage = {
      id: `temp-${Date.now()}`,
      user_id: user.id,
      username: playerName,
      message: text,
      created_at: new Date().toISOString(),
      role: "user",
      is_admin: false,
    };

    setMessages((prev) => [...prev, tempMessage].slice(-30));
    setNewMessage("");
    scrollToBottom(true);

    await supabase.from("chat_messages").insert({
      user_id: user.id,
      username: playerName,
      message: text,
      role: "user",
      is_admin: false,
    });
  };

  const latestSuperAdmin = messages
    .filter((m) => m.role === "super_admin")
    .slice(-1);
  const normalMessages = messages.filter((m) => m.role !== "super_admin");

  return (
    <div className="flex flex-col w-full h-full max-h-[70vh] sm:max-h-full bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 rounded-2xl shadow-[0_8px_0_#be185d,0_12px_20px_rgba(190,24,93,0.4)] overflow-hidden border-2 border-pink-200">
      {/* Chat contents */}
      <>
        {/* Super Admin Announcement */}
        {latestSuperAdmin.length > 0 && (
          <div className="bg-gradient-to-r from-yellow-100 via-yellow-200 to-orange-100 border-b-2 border-yellow-400 p-2 sm:p-3 text-[10px] sm:text-sm shadow-md flex-shrink-0">
            {latestSuperAdmin.map((msg) => (
              <div key={msg.id} className="rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 bg-white/50 shadow-md">
                <span className="font-black text-red-700 text-xs sm:text-sm">📢 {msg.username}</span>
                <div className="text-gray-900 break-words whitespace-pre-wrap mt-1 font-medium text-[10px] sm:text-sm">
                  {msg.message}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Chat Body */}
        <div
          ref={scrollContainerRef}
          className="flex-1 overflow-y-auto p-2 sm:p-4 text-[10px] sm:text-sm space-y-2 sm:space-y-3 bg-gradient-to-b from-white/80 to-pink-50/50"
        >
          {normalMessages.map((msg, i) => {
            const prevMsg = normalMessages[i - 1];
            const showUser = !prevMsg || prevMsg.user_id !== msg.user_id;

            return (
              <div
                key={msg.id}
                className="bg-white/90 border-2 border-pink-200 rounded-xl px-2 sm:px-3 py-1.5 sm:py-2 shadow-[0_3px_0_#f472b6] hover:shadow-[0_5px_0_#f472b6] hover:translate-y-[-2px] transition-all animate-[fadeIn_0.3s_ease]"
              >
                {showUser && (
                  <span className="font-black text-candyBlue block text-[10px] sm:text-sm mb-0.5 sm:mb-1">
                    {msg.username} {msg.is_admin && "👑"}
                  </span>
                )}
                <div className="text-gray-800 break-words whitespace-pre-wrap text-[10px] sm:text-sm">
                  {msg.message}
                </div>
                <div className="text-[8px] sm:text-[10px] text-gray-400 mt-0.5 sm:mt-1">
                  {new Date(msg.created_at).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>

        {/* Sticky Input */}
        <div className="sticky bottom-0 left-0 right-0 flex gap-1.5 sm:gap-2 border-t-2 border-pink-300 bg-gradient-to-r from-pink-100 to-purple-100 p-2 sm:p-3 flex-shrink-0">
          <input
            className="flex-1 p-2 sm:p-3 text-xs sm:text-sm outline-none border-2 border-pink-300 rounded-xl focus:border-pink-500 focus:ring-2 focus:ring-pink-400/30 transition bg-white"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && sendMessage()}
            placeholder="Type your message..."
          />
          <button
            onClick={sendMessage}
            className="px-3 sm:px-5 py-2 sm:py-3 text-base sm:text-lg bg-gradient-to-b from-pink-400 via-pink-500 to-pink-600 text-white rounded-full hover:scale-105 active:scale-95 active:translate-y-1 transition-transform shadow-[0_4px_0_#be185d] active:shadow-[0_2px_0_#be185d] font-bold"
          >
            📩
          </button>
        </div>
      </>
    </div>
  );
}
